/**
 * DSettings > "Polar Tracking" tab (AutoCAD Drafting Settings parity).
 *
 * Controls:
 *   - POLARMODE enable checkbox (F10) — bound to the sysvar store.
 *   - POLARANG increment dropdown — the polar angle increment (90/45/30/22.5/
 *     18/15/10/5, plus the live value if a custom SETVAR angle is set), bound to
 *     the real-valued POLARANG sysvar.
 *   - Additional-angles editor (AutoCAD POLARADDANG) — a checkbox that enables a
 *     user list of extra tracking angles, parsed from a ";"/"," separated field
 *     into the typed `polarTrackingPrefs` store (up to 10 angles).
 *   - Polar Angle Measurement (N6 POLAR-RELATIVE-BASIS-WIRE) — Absolute /
 *     Relative to Last Segment radio group, AutoCAD Drafting Settings parity
 *     (Autodesk "Polar Tracking Tab" reference). Bound to POLARMODE's
 *     app-extension relative-basis bit (`POLARMODE_RELATIVE_BASIS_BIT`) via
 *     `selectPolarAngleBasis` — see that constant's doc for why it is bit 16
 *     rather than AutoCAD's real bit 1 (already the master on/off switch here).
 *
 * The increment + additional angles + measurement basis feed the authoritative
 * typed `PolarTrackingConfig` consumed by the drafting-lock constraint — no CAD
 * semantics live in this component; it only edits the typed sources.
 */

import { useEffect, useState } from "react";

import { useT } from "../../i18n";
import {
  useSysvarStore,
  POLARMODE_ADDITIONAL_ANGLES_BIT,
  POLARMODE_TRACKING_ON_BIT,
  POLARMODE_RELATIVE_BASIS_BIT,
} from "../sysvarStore";
import {
  usePolarTrackingPrefs,
  parsePolarAddAng,
  formatPolarAddAng,
} from "../../state/polarTrackingPrefs";

/** AutoCAD's polar-angle increment menu (degrees). */
const POLAR_INCREMENTS = [90, 45, 30, 22.5, 18, 15, 10, 5];

export function PolarTab() {
  const t = useT();
  const polarMode = useSysvarStore((s) => s.values.POLARMODE);
  const polarAng = useSysvarStore((s) => s.values.POLARANG);
  const setVar = useSysvarStore((s) => s.setVar);

  const additionalAngles = usePolarTrackingPrefs((s) => s.additionalAnglesDeg);
  // "Use additional angles" is POLARMODE bit 4 (POLARADDANG), the single source
  // of truth — so a DSETTINGS toggle round-trips through SETVAR / query (#492 P2).
  const useAdditional = (polarMode & POLARMODE_ADDITIONAL_ANGLES_BIT) !== 0;
  const setAdditionalAngles = usePolarTrackingPrefs(
    (s) => s.setAdditionalAngles,
  );
  // N6 — POLARMODE's app-extension relative-basis bit (see that constant's
  // own doc for why it is bit 16, not AutoCAD's real bit 1). Derived from the
  // already-subscribed `polarMode`, same pattern as `useAdditional` above.
  const isRelativeBasis = (polarMode & POLARMODE_RELATIVE_BASIS_BIT) !== 0;

  // Local editable text mirrors the canonical list; committed (parsed +
  // normalised) on change/blur so mid-edit tokens like "22." are not clobbered.
  const [addText, setAddText] = useState(() =>
    formatPolarAddAng(additionalAngles),
  );
  // Resync when the canonical list changes from outside this field (e.g. reset).
  useEffect(() => {
    setAddText(formatPolarAddAng(additionalAngles));
  }, [additionalAngles]);

  // Ensure the live POLARANG is always selectable even if a custom SETVAR value.
  const options = POLAR_INCREMENTS.includes(polarAng)
    ? POLAR_INCREMENTS
    : [...POLAR_INCREMENTS, polarAng].sort((a, b) => b - a);

  const commitAdditional = (raw: string) => {
    const parsed = parsePolarAddAng(raw);
    setAdditionalAngles(parsed);
    setAddText(formatPolarAddAng(parsed));
  };

  return (
    <div className="dsettings-tab-content">
      <div className="dsettings-section">
        <div className="dsettings-section-title">
          {t("dsettings.tab.polar")}
        </div>
        <label className="dsettings-checkbox-row">
          <input
            type="checkbox"
            checked={(polarMode & POLARMODE_TRACKING_ON_BIT) !== 0}
            onChange={(e) =>
              // Toggle ONLY the master bit; preserve the config bits (POLARADDANG
              // etc.) so unticking "Polar Tracking On" keeps the additional-angle
              // setup, matching AutoCAD's Drafting Settings dialog (#492 @codex P2).
              setVar(
                "POLARMODE",
                e.target.checked
                  ? polarMode | POLARMODE_TRACKING_ON_BIT
                  : polarMode & ~POLARMODE_TRACKING_ON_BIT,
              )
            }
          />
          <span className="dsettings-checkbox-label">
            {t("dsettings.polar.enable")}
          </span>
        </label>

        <div className="dsettings-field-row">
          <label htmlFor="dsettings-polar-increment">
            {t("dsettings.polar.increment.label")}
          </label>
          <select
            id="dsettings-polar-increment"
            data-testid="dsettings-polar-increment"
            value={String(polarAng)}
            onChange={(e) => setVar("POLARANG", Number(e.target.value))}
          >
            {options.map((deg) => (
              <option key={deg} value={String(deg)}>
                {String(deg)}
              </option>
            ))}
          </select>
        </div>

        <label className="dsettings-checkbox-row">
          <input
            type="checkbox"
            data-testid="dsettings-polar-additional-enable"
            checked={useAdditional}
            onChange={(e) =>
              setVar(
                "POLARMODE",
                e.target.checked
                  ? polarMode | POLARMODE_ADDITIONAL_ANGLES_BIT
                  : polarMode & ~POLARMODE_ADDITIONAL_ANGLES_BIT,
              )
            }
          />
          <span className="dsettings-checkbox-label">
            {t("dsettings.polar.additional.enable")}
          </span>
        </label>
        <div className="dsettings-field-row">
          <input
            type="text"
            data-testid="dsettings-polar-additional-list"
            aria-label={t("dsettings.polar.additional.enable")}
            placeholder={t("dsettings.polar.additional.placeholder")}
            disabled={!useAdditional}
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            onBlur={(e) => commitAdditional(e.target.value)}
          />
        </div>

        <p className="dsettings-hint">{t("dsettings.polar.additional.hint")}</p>
        <p className="dsettings-hint">{t("dsettings.polar.hint")}</p>
      </div>

      <div className="dsettings-section">
        <div className="dsettings-section-title">
          {t("dsettings.polar.basis.title")}
        </div>
        <label className="dsettings-radio-row">
          <input
            type="radio"
            name="polar-basis"
            data-testid="dsettings-polar-basis-absolute"
            checked={!isRelativeBasis}
            onChange={() =>
              setVar("POLARMODE", polarMode & ~POLARMODE_RELATIVE_BASIS_BIT)
            }
          />
          <span>{t("dsettings.polar.basis.absolute")}</span>
        </label>
        <label className="dsettings-radio-row">
          <input
            type="radio"
            name="polar-basis"
            data-testid="dsettings-polar-basis-relative"
            checked={isRelativeBasis}
            onChange={() =>
              setVar("POLARMODE", polarMode | POLARMODE_RELATIVE_BASIS_BIT)
            }
          />
          <span>{t("dsettings.polar.basis.relative")}</span>
        </label>
      </div>
    </div>
  );
}
